import { Children, CSSProperties, ReactNode } from 'react'
import classnames from 'classnames'
import { IconTypes } from '~ui/Icon'
import { TextCol, Grid, Flex, Image, Button, Heading, Icon } from '~ui'
import { Fields } from '~ui/TextCol'
import { useDataLayer, useQueryParams } from '~hooks'
import { hasAnElementAsParent } from '~utils'

import styles from './Item.module.scss'

export type ItemProps = {
  fields: Fields[]
  imageSrc?: string
  iconForMobile?: IconTypes
  link?: string
  date?: string[]
  eventSlug?: string
  onlyVip?: boolean
  external?: boolean
  divider?: boolean
  actions: Array<ReactNode>
  isMobile?: boolean
  isEvent?: boolean
  sellingFast?: boolean
}

const SellingFastLabel = () => (
  <Heading Tag="span" colorGlow="red" className={styles['selling-fast-label']}>
    <Icon
      name="lightning-v2"
      glowColor="red"
      style={{
        width: '1em',
        height: '1em',
      }}
      className={styles.icon}
    />
    This event is selling fast
  </Heading>
)

const Item: React.FC<ItemProps> = ({
  fields,
  imageSrc,
  link,
  divider,
  date,
  actions,
  onlyVip,
  isMobile,
  isEvent = true,
  sellingFast,
}) => {
  const dl = useDataLayer()
  const { push } = useQueryParams()

  const imageAlt = fields && fields[0] && fields[0][0]?.text
  const isImage = !!imageSrc
  const fieldsLen = fields && fields.length

  const hasAnchorParent = (target) => hasAnElementAsParent(target, 'A')

  return (
    <Flex flexDirection="column" width="100%" className={classnames(styles.cell)}>
      {isMobile ? (
        <>
          <Button
            mode="button"
            uppercase={false}
            className={classnames(styles['cell-button'], styles['cell-mobile'])}
            onClick={() => {
              dl.push({ event: 'click_on_event_cell' })
              push(link)
            }}
          >
            <div
              className={classnames(styles.item, styles['item-mobile'])}
              style={
                {
                  '--template': `${isImage ? 'auto' : ''} ${fieldsLen < 2 ? '1fr' : '125px 1fr'} ${
                    !isImage ? 'auto' : ''
                  }`,
                } as CSSProperties
              }
            >
              {isImage && (
                <Image
                  wrapperClassName={styles.image}
                  src={imageSrc}
                  alt={imageAlt}
                  height={75}
                  width={75}
                />
              )}
              {date && (
                <div className={styles['date-label']}>
                  <Heading Tag="span" uppercase color="light-gray">
                    {date[0]}
                  </Heading>
                  <Heading Tag="span" className={styles.middle} uppercase>
                    {date[1]}
                  </Heading>
                  <Heading Tag="span" uppercase color="light-gray">
                    {date[2]}
                  </Heading>
                </div>
              )}
              <Grid className={styles['text-fields']}>
                {sellingFast && <SellingFastLabel />}
                {fields &&
                  fields.map((field, key) => (
                    <TextCol
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${key}-${field && field[0].text}`}
                      fields={field}
                    />
                  ))}
                {isEvent && (
                  <Heading Tag="span" colorGlow="g-green-darker">
                    <Grid gridAutoFlow="column" gap="8px" width="fit-content" placeItems="center">
                      <Icon
                        name="ticket-thin"
                        glowColor="g-green-darker"
                        width="1.1em"
                        height="1.1em"
                      />
                      {onlyVip ? 'View Packages' : 'Get Tickets'}
                    </Grid>
                  </Heading>
                )}
              </Grid>
            </div>
          </Button>
        </>
      ) : (
        <>
          <Button
            mode="button"
            uppercase={false}
            onClick={(e) => {
              if (e.target.tagName !== 'A' && !hasAnchorParent(e.target)) {
                // do not execute on anchor click(passed as actions) && check if it has link as parent
                // layout of Link: <a> -> <span> -> <svg> -> <path> - you clicked on span or path inside a link
                dl.push({ event: 'click_on_event_cell' })
                push(link)
              }
            }}
            className={styles['link-style']}
          >
            <div className={styles.item}>
              <div>
                <div
                  style={{
                    paddingBottom: 12,
                  }}
                >
                  {sellingFast && <SellingFastLabel />}
                </div>
                <div
                  className={styles.item}
                  style={
                    {
                      '--template': `${isImage ? '100px' : ''} ${
                        fieldsLen < 2 ? '1fr' : '130px auto'
                      } auto`,
                      padding: 0,
                    } as CSSProperties
                  }
                >
                  {isImage && (
                    <Image
                      wrapperClassName={styles.image}
                      src={imageSrc}
                      alt={imageAlt}
                      height={100}
                      width={100}
                    />
                  )}
                  {fields &&
                    fields.map((field, key) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <TextCol key={`${key}-${field && field[0].text}`} fields={field} />
                    ))}
                </div>
              </div>
              <Grid className={styles.buttons} gap="1.5rem" gridAutoFlow="column" justifySelf="end">
                {Children.map(actions, (child) => child)}
              </Grid>
            </div>
          </Button>
        </>
      )}
      {divider && <div className={styles.separator} />}
    </Flex>
  )
}

export default Item
